import Container from 'components/Modal/Container';
import { baseURL, workerUrl } from 'constants/config';
import { useAuthentication, useFetch, useRouter } from 'hooks';
import { Message } from 'i18n';
import { FormattedMessage } from 'react-intl';
import { propertyRoomIcons } from 'store/constants/user-management/properties.constants';
import styled from 'styled-components';
import rtl from 'styled-components-rtl';
import { isMobile } from 'react-device-detect';
import React, { useEffect } from 'react';
import { METHODS } from 'api/client';
import Loader from 'components/Loader/Loader';
import Modal from 'components/@client/Modal/Modal';
import { Button } from '@material-ui/core';
import { formatDate } from 'utils/dates.utils';
import { ClientSelectFilter } from 'components/Filters';
import { ClientCheckboxItem } from 'components/Filters/Options';
import { Option } from 'components/Autocomplete/Autocomplete.config';

interface Props {
  isOpen: boolean;
  onClose: VoidFunction;
  propertyId: string;
}

interface Room {
  name: string;
  products: Product[];
}

interface Product {
  id: number;
  name: string;
  category: string;
  sku: string;
  thumbnail_url: string;
  type: 'model' | 'material';
  quantity: number;
  external_part: boolean;
  supplier: {
    id: number;
    company_name: string;
  };
}

interface PDFData {
  rooms: Room[];
  floor_plan_url: string;
  property_name: string;
  property_id: string;
}
// eslint-disable-next-line
let _window: any = window;
const RoomProductsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  propertyId,
}) => {
  const [isPdfModalOpen, setIsPdfModalOpen] = React.useState(false);
  const { supplierId, seller_supplier } = useAuthentication();
  const [selectedSupplierIds, setSelectedSupplierIds] = React.useState<
    string[]
  >([]);
  const { history } = useRouter();

  useEffect(() => {
    if (supplierId) {
      setSelectedSupplierIds([(supplierId ?? seller_supplier).toString()]);
    } else if (seller_supplier) {
      setSelectedSupplierIds([seller_supplier.toString()]);
    }
  }, [supplierId, seller_supplier]);

  const { data: pdfData, isLoading } = useFetch<PDFData>({
    initialUrl: `${workerUrl}/pdf/data?propertyId=${propertyId}`,
  });

  const { data: isProductsPdfExists } = useFetch<{
    url: string;
    lastModified: string;
  }>({
    initialUrl: `${workerUrl}/pdf/existing?propertyId=${propertyId}`,
    config: { method: METHODS.GET },
  });

  // Mock supplier data
  const mockSuppliers = React.useMemo(
    () => [
      { id: 1, company_name: 'IKEA' },
      { id: 2, company_name: 'Home Depot' },
      { id: 3, company_name: 'Wayfair' },
      { id: 4, company_name: 'West Elm' },
      { id: 5, company_name: 'CB2' },
      { id: 6, company_name: 'Pottery Barn' },
      { id: 7, company_name: 'Crate & Barrel' },
      { id: 8, company_name: 'Target' },
    ],
    []
  );

  // Get unique suppliers from all products across all rooms
  const supplierOptions = React.useMemo(() => {
    if (!pdfData?.rooms) return [];

    const uniqueSuppliers = new Map<number, string>();

    pdfData.rooms.forEach(room => {
      room.products.forEach(product => {
        if (product.supplier?.id && product.supplier?.company_name) {
          uniqueSuppliers.set(
            product.supplier.id,
            product.supplier.company_name
          );
        }
      });
    });

    // If no suppliers found in actual data, use mock data
    if (uniqueSuppliers.size === 0) {
      mockSuppliers.forEach(supplier => {
        uniqueSuppliers.set(supplier.id, supplier.company_name);
      });
    }

    return Array.from(uniqueSuppliers.entries()).map(([id, name]) => ({
      name,
      value: id.toString(),
    }));
  }, [pdfData?.rooms, mockSuppliers]);

  // Filter products by selected suppliers
  const filteredRooms = React.useMemo(() => {
    if (!pdfData?.rooms || selectedSupplierIds.length === 0)
      return pdfData?.rooms || [];

    return pdfData.rooms
      .map(room => ({
        ...room,
        products: room.products.filter(product => {
          return (
            product.supplier?.id &&
            selectedSupplierIds.includes(product.supplier.id.toString())
          );
        }),
      }))
      .filter(room => room.products.length > 0);
  }, [pdfData?.rooms, selectedSupplierIds]);

  const onSupplierApply = React.useCallback((values: Option[]) => {
    setSelectedSupplierIds(
      values.map(v => v?.value?.toString()).filter(Boolean) as string[]
    );
  }, []);

  const {
    // data: pdfDownloadLink,
    isLoading: isPdfGenerating,
    callFetch: generatePdf,
  } = useFetch<{ url: string }>({
    initialUrl: `${workerUrl}/pdf/generate?propertyId=${propertyId}`,
    skipOnStart: true,
    config: { method: METHODS.POST },
    onSuccess: res => {
      setIsPdfModalOpen(false);
      if (res?.url) window.open(res.url ?? '', '_blank');
    },
  });

  return (
    <Root>
      <Container
        $width="90vw"
        $borderRadius="16px"
        open={isOpen}
        onBackdropClick={onClose}
        disablePortal
      >
        <Content>
          {isPdfGenerating ||
            (isLoading && (
              <LoaderWrapper>
                <Loader />
              </LoaderWrapper>
            ))}

          <ActionsContainer>
            <SupplierFilterContainer>
              {pdfData && (
                <ClientSelectFilter
                  options={supplierOptions}
                  placeholder="Filter by Supplier"
                  selectedPlaceholder="Filter by Supplier"
                  onApply={onSupplierApply}
                  renderOption={ClientCheckboxItem}
                  multiple={true}
                  disableSelectAll
                  customIcon={true}
                  buttonClassName="selectButton"
                  initialState={
                    supplierId || seller_supplier
                      ? supplierOptions.filter(
                          option =>
                            option.value ===
                            (supplierId ?? seller_supplier)?.toString()
                        )
                      : undefined
                  }
                  placement="bottom"
                  modifiers={[
                    {
                      name: 'flip',
                      enabled: true,
                      options: {
                        altBoundary: false,
                        rootBoundary: 'document',
                        padding: 8,
                      },
                    },
                  ]}
                  popperOptions={{
                    strategy: 'fixed',
                  }}
                />
              )}
            </SupplierFilterContainer>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                if (isProductsPdfExists) {
                  setIsPdfModalOpen(true);
                } else {
                  generatePdf({
                    url: `${workerUrl}/pdf/generate`,
                    params: {
                      supplierId: selectedSupplierIds,
                      propertyId,
                    },
                  });
                }
              }}
            >
              Download PDF
            </Button>
          </ActionsContainer>
          <ProductsContainer>
            {filteredRooms.map(room => (
              <div key={room.name} className="room-container">
                <div className="room-info">
                  <img
                    src={propertyRoomIcons[room.name.toLowerCase()]}
                    alt={room.name}
                  />
                  <h3>{room.name}</h3>
                </div>
                <div className="room-products">
                  {room.products.map(product => (
                    <div
                      key={product.id}
                      className="product-item"
                      onClick={() => {
                        if (isMobile || _window.vuplex) {
                          if (_window.vuplex) {
                            _window.vuplex.postMessage({
                              product_Id: product.id,

                              pause: false,

                              baseEp: `${baseURL}/api`,
                            });
                          } else {
                            _window.open(
                              `https://patriciaai.page.link/?link=https://patriciaai.page.link/product-preview?product_Id%3D${product.id}%26pause%3Dfalse%26baseEp%3D${baseURL}/api&apn=com.patriciaai.com.PatriciaTourPlayer&isi=1597629731&ibi=com.patricia-ai.com.PatriciaTourPlayer`,
                              '_blank'
                            );
                          }
                        } else {
                          history.push(`?product3dView=${product.id}`);
                        }
                      }}
                    >
                      <div className="product-details">
                        <span>
                          {product.name.length > 25
                            ? `${product.name.slice(0, 25)}…`
                            : product.name}
                        </span>
                        <br />
                        {product.quantity > 1 && (
                          <span>
                            <b>
                              <FormattedMessage
                                id={Message.CHECKOUT_ITEM_QUANTITY_LABEL}
                              />{' '}
                              :
                            </b>
                            {product.type === 'material'
                              ? `${product.quantity.toFixed(2)} m²`
                              : product.quantity}
                          </span>
                        )}
                      </div>
                      <img src={product.thumbnail_url} alt={product.name} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </ProductsContainer>
        </Content>
      </Container>
      {isPdfModalOpen && (
        <Modal
          onClose={() => setIsPdfModalOpen(false)}
          actions={[
            {
              label: (
                <ExistingButtonContainer>
                  <FormattedMessage id={Message.DOWNLOAD_EXISTING_PDF} />
                  <span>
                    <b>
                      <FormattedMessage id={Message.PDF_CREATED_DATE_AT} />:
                    </b>
                    {` ${formatDate(isProductsPdfExists?.lastModified)}`}
                  </span>
                </ExistingButtonContainer>
              ),
              onClick: () => {
                if (isProductsPdfExists) {
                  window.open(isProductsPdfExists.url, '_blank');
                }
              },
              isLoading: isPdfGenerating,

              style: { width: '290px', height: '60px', maxWidth: '300px' },
            },
            {
              label: <FormattedMessage id={Message.GENERATE_NEW_PDF} />,
              onClick: () => {
                generatePdf({
                  url: `${workerUrl}/pdf/generate`,
                  params: {
                    supplierId: selectedSupplierIds,
                    propertyId,
                  },
                });
                setIsPdfModalOpen(false);
              },
              isLoading: isPdfGenerating,
              disabled: isPdfGenerating,
              style: { width: '290px', height: '60px', maxWidth: '300px' },
              variant: 'secondary',
            },
          ]}
          open={isPdfModalOpen}
        >
          <Content>
            <FormattedMessage id={Message.DOWNLOAD_OR_GENERATE_PDF} />
          </Content>
        </Modal>
      )}
    </Root>
  );
};

export default RoomProductsModal;

const ActionsContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  justify-content: space-between;
  align-items: center;
  button {
    background-color: #fc5c42;
  }
`;

const SupplierFilterContainer = styled.div`
  .selectButton {
    border: 1px solid #ccc;
    background-color: #ffffff;
    color: #676766;
    font-size: 14px;
    font-weight: 500;
    padding: 8px 16px;
    border-radius: 4px;
    min-width: 200px;
    &:hover {
      color: #fc5c42;
      border-color: #fc5c42;
    }
  }
  .isopen {
    .dropdown-placeholder {
      color: #fc5c42 !important;
    }
    border-color: #fc5c42;
  }
`;

const ExistingButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 14px;
`;

const Root = styled.div.attrs({ className: 'modal-root' })`
  && {
    .dialog-space {
      padding: 0 0 20px 0;
      height: auto;
      z-index: 100;
    }

    .dialog-backdrop {
      background: rgba(92, 87, 87, 0.57);
    }

    .MuiDialog-root {
      z-index: 1000 !important;
      margin-top: 60px;
    }
    .MuiPaper-root {
      height: 90vh;
    }
  }
`;

const LoaderWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  display: flex;
  justify-content: center;

  align-items: center;
  background-color: rgba(255, 255, 255, 0.8);
  z-index: 1000;
`;

const Content = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
  padding: 20px;
  text-align: center;
  //   margin-top: 100px;
  && {
    .close-button {
      position: absolute;
      top: 24px;
      ${rtl`
      right: 30px;
      `}

      .close-icon {
        font-size: 38px;
        opacity: 1;
      }
    }
  }
`;

const ProductsContainer = styled.div`
  .room-container {
    display: flex;
    position: relative;
    border: 1px solid #ccc;
    padding: 20px 8px;
    overflow: hidden;
    .room-info {
      width: 15%;
      height: inherit;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 20px;
      img {
        width: 200px;
        height: 200px;
      }
    }
    .room-products {
      width: 85%;
      display: grid;
      grid-template-columns: repeat(4, 1fr);

      row-gap: 30px;
      cursor: pointer;
      .product-item {
        max-width: 350px;
        display: flex;
        justify-content: space-between;
        .product-details {
          ${rtl`
            text-align: left;
          `}
        }
        img {
          width: 100px;
          height: 100px;
          object-fit: cover;
        }
      }
    }
  }
`;
